import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { sendContactEmail } from '../services/emailService';
import SuccessModal from '../components/SuccessModal';
import '../styles/contact.css';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { ScrollProgress } from '../components/ui/scroll-progress';
import { Marquee } from '../components/ui/marquee';
import { Particles } from '../components/ui/particles';
import { BentoGrid, BentoCard } from '../components/ui/bento-grid';
import { AnimatedBeamCard } from '../components/ui/animated-beam-card';
import { MarqueeHorizontalBg, MarqueeVerticalBg } from '../components/ui/marquee-cards';
import { ChatDemoCard } from '../components/ui/chat-demo';
import { ShineBorder } from '../components/ui/shine-border';
import { HoverButton } from '../components/ui/hover-button';
import { GhostHoverButton } from '../components/ui/ghost-hover-button';

/* Reusable animation variants */
const fadeUp = {
    hidden: { opacity: 0, y: 32 },
    visible: (i = 0) => ({
        opacity: 1,
        y: 0,
        transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1], delay: i * 0.1 },
    }),
};

const staggerContainer = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.1 } },
};

/* Animated section wrapper */
const Section = ({ children, className = '', id, style }) => {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, margin: '-80px' });
    return (
        <motion.section
            id={id}
            ref={ref}
            className={className}
            style={style}
            variants={staggerContainer}
            initial="hidden"
            animate={inView ? 'visible' : 'hidden'}
        >
            {children}
        </motion.section>
    );
};

const LandingPage = () => {
    const navigate = useNavigate();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [activeSection, setActiveSection] = useState('hero');

    useEffect(() => {
        const sectionIds = ['recursos', 'planos'];

        const handleScroll = () => {
            if (window.scrollY < 100) {
                setActiveSection('hero');
                return;
            }
            for (const id of sectionIds) {
                const el = document.getElementById(id);
                if (!el) continue;
                const rect = el.getBoundingClientRect();
                if (rect.top <= 120 && rect.bottom > 120) {
                    setActiveSection(id);
                    return;
                }
            }
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        handleScroll();
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const [formData, setFormData] = useState({
        fullName: '',
        email: '',
        phone: '',
        company: '',
        service: '',
        message: '',
    });

    const [formErrors, setFormErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null);
    const [showSuccessModal, setShowSuccessModal] = useState(false);

    const validateField = (name, value) => {
        const errors = { ...formErrors };
        switch (name) {
            case 'fullName':
                if (!value.trim()) errors.fullName = 'Nome completo é obrigatório';
                else if (value.trim().length < 2) errors.fullName = 'Nome deve ter pelo menos 2 caracteres';
                else delete errors.fullName;
                break;
            case 'email': {
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (!value.trim()) errors.email = 'E-mail é obrigatório';
                else if (!emailRegex.test(value)) errors.email = 'E-mail inválido';
                else delete errors.email;
                break;
            }
            case 'phone': {
                const phoneRegex = /^\(\d{2}\)\s\d{4,5}-\d{4}$/;
                if (!value.trim()) errors.phone = 'Telefone é obrigatório';
                else if (!phoneRegex.test(value)) errors.phone = 'Formato: (99) 99999-9999';
                else delete errors.phone;
                break;
            }
            case 'message':
                if (!value.trim()) errors.message = 'Mensagem é obrigatória';
                else if (value.trim().length < 10) errors.message = 'Mensagem deve ter pelo menos 10 caracteres';
                else delete errors.message;
                break;
            default:
                break;
        }
        setFormErrors(errors);
    };

    const formatPhone = (value) => {
        const numbers = value.replace(/\D/g, '');
        if (numbers.length <= 11) return numbers.replace(/(\d{2})(\d{4,5})(\d{4})/, '($1) $2-$3');
        return value;
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        let formattedValue = value;
        if (name === 'phone') formattedValue = formatPhone(value);
        setFormData((prev) => ({ ...prev, [name]: formattedValue }));
        validateField(name, formattedValue);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        Object.keys(formData).forEach((key) => {
            if (['fullName', 'email', 'phone', 'message'].includes(key)) validateField(key, formData[key]);
        });
        const hasErrors = Object.keys(formErrors).length > 0;
        const hasEmptyRequired = !formData.fullName || !formData.email || !formData.phone || !formData.message;
        if (hasErrors || hasEmptyRequired) { setSubmitStatus('error'); return; }
        setIsSubmitting(true);
        setSubmitStatus(null);
        try {
            await sendContactEmail(formData);
            setSubmitStatus('success');
            setShowSuccessModal(true);
            setFormData({ fullName: '', email: '', phone: '', company: '', service: '', message: '' });
            setFormErrors({});
        } catch (error) {
            setSubmitStatus('error');
        } finally {
            setIsSubmitting(false);
        }
    };

    const scrollToContact = () => {
        document.getElementById('contato')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const scrollTo = (id) => {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
        setMobileMenuOpen(false);
    };

    const navLinks = [
        { label: 'Início', id: 'hero' },
        { label: 'Recursos', id: 'recursos' },
        { label: 'Preços', id: 'planos' },
    ];

    return (
        <div
            className="min-h-screen text-on-surface overflow-x-hidden font-body selection:bg-primary-container selection:text-on-primary-container relative"
            style={{
                backgroundColor: '#131315',
                /* Force dark token values locally overrides html.dark state */
                '--color-background': '#131315',
                '--color-surface': '#131315',
                '--color-surface-dim': '#131315',
                '--color-surface-container': '#201f21',
                '--color-surface-container-low': '#1b1b1d',
                '--color-surface-container-high': '#2a2a2c',
                '--color-surface-container-highest': '#353437',
                '--color-surface-container-lowest': '#0e0e10',
                '--color-surface-variant': '#353437',
                '--color-on-surface': '#e5e1e4',
                '--color-on-surface-variant': '#bbcbb8',
                '--color-on-background': '#e5e1e4',
                '--color-primary': '#51e1a5',
                '--color-primary-container': '#2ac48b',
                '--color-on-primary': '#003824',
                '--color-on-primary-container': '#004b32',
                '--color-tertiary': '#d1c0ff',
                '--color-on-tertiary': '#381385',
                '--color-tertiary-container': '#b79fff',
                '--color-outline': '#869583',
                '--color-outline-variant': '#3c4a3c',
                '--color-error': '#ffb4ab',
            }}
        >

            {/* PARTICLES BACKGROUND */}
            <Particles
                className="fixed inset-0 z-0 w-full h-full"
                quantity={120}
                staticity={30}
                ease={80}
                size={0.5}
                color="#51e1a5"
                vx={0}
                vy={0}
            />

            {/* NAVBAR  */}
            <nav className="fixed top-0 w-full z-50 backdrop-blur-xl shadow-sm"
                style={{ backgroundColor: 'rgba(19,19,21,0.80)' }}>
                <div className="flex justify-between items-center px-8 py-4 max-w-[1365px] mx-auto">
                    {/* Logo */}
                    <span className="text-2xl font-black text-white tracking-tighter font-headline">
                        PapIA
                    </span>

                    {/* Desktop nav */}
                    <div className="hidden md:flex gap-8 items-center">
                        {[
                            { label: 'Início', id: 'hero' },
                            { label: 'Recursos', id: 'recursos' },
                            { label: 'Preços', id: 'planos' },
                        ].map(({ label, id }) => (
                            <button
                                key={id}
                                onClick={() => scrollTo(id)}
                                className={`font-headline font-bold tracking-tight text-sm pb-1 transition-colors duration-300 ${activeSection === id
                                    ? 'text-primary border-b-2 border-primary'
                                    : 'text-slate-400 hover:text-primary'
                                    }`}
                            >
                                {label}
                            </button>
                        ))}
                    </div>

                    {/* CTA */}
                    <div className="flex gap-4 items-center">
                        <GhostHoverButton
                            onClick={() => navigate('/login')}
                            className="px-6 py-2 text-sm font-headline font-extrabold tracking-tight"
                            style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.2)' }}
                        >
                            Entrar
                        </GhostHoverButton>
                        {/* Mobile hamburger */}
                        <button
                            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                            className="md:hidden p-2 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 transition-colors"
                            aria-label="Menu"
                        >
                            <span className="material-symbols-outlined">
                                {mobileMenuOpen ? 'close' : 'menu'}
                            </span>
                        </button>
                    </div>
                </div>

                {/* Mobile menu */}
                <AnimatePresence>
                    {mobileMenuOpen && (
                        <motion.div
                            className="md:hidden border-t border-white/10 backdrop-blur-xl"
                            style={{ backgroundColor: 'rgba(19,19,21,0.98)' }}
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.25 }}
                        >
                            <div className="px-8 py-4 flex flex-col gap-1">
                                {navLinks.map((link) => (
                                    <button
                                        key={link.id}
                                        onClick={() => scrollTo(link.id)}
                                        className="text-left py-3 px-4 rounded-lg text-slate-300 hover:text-white hover:bg-white/5 transition-colors font-medium"
                                    >
                                        {link.label}
                                    </button>
                                ))}
                                <div className="mt-3 pt-3 border-t border-white/10">
                                    <HoverButton
                                        onClick={scrollToContact}
                                        className="w-full py-2.5 text-sm font-extrabold border-primary/30"
                                        bgColor="var(--color-primary, #51e1a5)"
                                        textColor="var(--color-on-primary, #003824)"
                                        dotColor="var(--color-on-primary, #003824)"
                                        textHoverColor="#51e1a5"
                                    >
                                        Começar Agora
                                    </HoverButton>
                                </div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
                <ScrollProgress style={{ position: 'absolute', bottom: 0, top: 'auto', left: 0, right: 0 }} />
            </nav>

            <main className="max-w-[1365px] mx-auto relative">

                {/* �"?�"? HERO �"?�"? */}
                <section
                    id="hero"
                    className="relative pt-32 pb-48 px-8"
                >
                    {/* �"?�"? Emerald glow �?" full-width background, not clipped �"?�"? */}
                    <div className="absolute inset-0 pointer-events-none" style={{ left: 'calc(-50vw + 50%)', width: '100vw' }}>
                        <div className="absolute top-[10%] right-[10%] w-[700px] h-[700px] rounded-full blur-[180px]" style={{ backgroundColor: 'rgba(81,225,165,0.07)' }}></div>
                        <div className="absolute top-[15%] right-[15%] w-[500px] h-[500px] rounded-full blur-[120px]" style={{ backgroundColor: 'rgba(81,225,165,0.08)' }}></div>
                        <div className="absolute top-[20%] right-[20%] w-[350px] h-[350px] rounded-full blur-[80px]" style={{ backgroundColor: 'rgba(81,225,165,0.13)' }}></div>
                    </div>

                    {/* �"?�"? Diagonal bottom cut (replaces clipPath so glow isn't clipped) �"?�"? */}
                    <div className="absolute bottom-0 left-0 w-full h-[15%] pointer-events-none" style={{ left: 'calc(-50vw + 50%)', width: '100vw', background: 'linear-gradient(to right bottom, transparent 49.5%, #131315 50%)' }}></div>

                    <div className="grid grid-cols-1 md:grid-cols-5 gap-12 items-center relative z-10">
                        {/* Left column */}
                        <div className="md:col-span-3 space-y-6">
                            <motion.h1
                                className="text-5xl md:text-6xl font-extrabold tracking-tighter leading-none text-white font-headline"
                                variants={fadeUp}
                                custom={0}
                                initial="hidden"
                                animate="visible"
                            >
                                Automatize seu
                                <br />
                                WhatsApp e
                                <br />
                                seu Instagram
                            </motion.h1>
                            <motion.p
                                className="text-lg text-on-surface-variant max-w-lg leading-relaxed"
                                variants={fadeUp}
                                custom={1}
                                initial="hidden"
                                animate="visible"
                            >
                                Transforme seu atendimento ao cliente com automação inteligente. Economize tempo, aumente o engajamento e venda mais com mensagens personalizadas e automáticas no WhatsApp.
                            </motion.p>
                            <motion.div
                                className="flex flex-wrap gap-4 pt-4"
                                variants={fadeUp}
                                custom={2}
                                initial="hidden"
                                animate="visible"
                            >
                                <HoverButton
                                    onClick={scrollToContact}
                                    className="px-8 py-4 font-extrabold border-primary/30"
                                    bgColor="var(--color-primary, #51e1a5)"
                                    textColor="var(--color-on-primary, #003824)"
                                    dotColor="#001f13"
                                    textHoverColor="#fff"
                                    showDot={false}
                                    style={{ boxShadow: '0 8px 24px rgba(81,225,165,0.3)' }}
                                >
                                    Entrar em Contato
                                </HoverButton>
                                <GhostHoverButton
                                    onClick={() => scrollTo('recursos')}
                                    className="px-8 py-4"
                                    showDot
                                >
                                    Ver Demo
                                </GhostHoverButton>
                            </motion.div>
                        </div>

                        {/* Right column " dashboard card */}
                        <div className="md:col-span-2 relative">
                            <div className="relative z-10 p-4 bg-surface-container-high rounded-xl border border-outline-variant/20 shadow-2xl">
                                <img
                                    alt="Dashboard PapIA Analytics"
                                    className="rounded-lg shadow-inner w-full h-auto block"
                                    src="/dashboard.png"
                                />
                                {/* Glass badge */}
                                <div
                                    className="absolute -bottom-6 -left-6 p-4 rounded-lg shadow-xl border-b-2 border-primary"
                                    style={{ background: 'rgba(53,52,55,0.4)', backdropFilter: 'blur(12px)' }}
                                >
                                    <span className="text-primary font-black text-2xl tracking-tighter">200%</span>
                                    <p className="text-[10px] uppercase font-bold text-on-surface-variant tracking-widest">
                                        Aumento de Conversão
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* �"?�"? LOGO MARQUEE �"?�"? */}
                    <div className="relative mt-32 -mx-8">
                        <p className="text-center text-xs font-bold uppercase tracking-widest text-on-surface-variant/50 mb-6">
                            Confiado por empresas em crescimento
                        </p>
                        <div className="relative overflow-hidden">
                            <Marquee pauseOnHover style={{ '--duration': '60s' }}>
                                {[
                                    { name: 'S.A Casa de Sushi Maringá', logo: '/logos/samaringa.jpg', shape: 'circle' },
                                    { name: 'S.A Casa de Sushi Alto da XV', logo: '/logos/saaltodaxv.jpg', shape: 'circle' },
                                    { name: 'Budega do Vizinho', logo: '/logos/budega.jpg', shape: 'rounded' },
                                    { name: 'Kanoa Sushi', logo: '/logos/kanoa.jpg', shape: 'rounded' },
                                ].map(({ name, logo, shape }) => (
                                    <div key={name} className="mx-12 flex items-center gap-3 opacity-80 hover:opacity-100 transition-opacity">
                                        {shape === 'circle' ? (
                                            <div className="h-9 w-9 rounded-full overflow-hidden flex-shrink-0">
                                                <img src={logo} alt={name} className="w-full h-full object-cover" style={{ transform: 'scale(1.4)', transformOrigin: 'center' }} />
                                            </div>
                                        ) : (
                                            <img src={logo} alt={name} className="h-9 w-9 object-cover rounded-lg flex-shrink-0" />
                                        )}
                                        <span className="text-white font-bold text-lg tracking-tight whitespace-nowrap">{name}</span>
                                    </div>
                                ))}
                            </Marquee>
                            <div className="pointer-events-none absolute inset-y-0 left-0 w-32 bg-gradient-to-r from-surface" />
                            <div className="pointer-events-none absolute inset-y-0 right-0 w-32 bg-gradient-to-l from-surface" />
                        </div>
                    </div>
                </section>

                {/* BENTO */}
                <Section
                    id="solucoes"
                    className="clip-diagonal bg-surface-container-low pt-36 sm:pt-28 pb-24 px-4 sm:px-8 -mt-16 relative"
                    style={{ clipPath: 'polygon(0 15%, 100% 0, 100% 100%, 0% 100%)' }}
                >
                    <div className="text-center mt-4 sm:mt-12 mb-6 space-y-4">
                        <motion.h2
                            className="text-3xl font-extrabold tracking-tight font-headline"
                            variants={fadeUp}
                        >
                            Eficiência comprovada
                        </motion.h2>
                        <motion.p
                            className="text-on-surface-variant text-sm uppercase tracking-widest font-semibold"
                            variants={fadeUp}
                            custom={1}
                        >
                            Resultados melhores para a sua empresa
                        </motion.p>
                    </div>

                    <BentoGrid className="auto-rows-[20rem] md:auto-rows-[22rem]">
                        {/* 200% de Conversão — Marquee Vertical */}
                        <BentoCard
                            name="Reviews de nossos clientes"
                            description="Veja o que nossos clientes dizem sobre o PapIA"
                            className="col-span-3 lg:col-span-1"
                            Icon={({ className: c }) => (
                                <span className={`material-symbols-outlined text-primary text-4xl ${c || ''}`}>reviews</span>
                            )}
                            href="#contato"
                            cta="Entrar em contato"
                            background={<MarqueeVerticalBg />}
                        />

                        {/* Marquee Horizontal */}
                        <BentoCard
                            name="Informações do sistema"
                            description="Tudo o que o PapIA entrega — medido e verificado."
                            className="col-span-3 lg:col-span-2"
                            Icon={({ className: c }) => (
                                <span className={`material-symbols-outlined text-primary text-4xl ${c || ''}`}>verified_user</span>
                            )}
                            href="#recursos"
                            cta="Saiba mais"
                            background={<MarqueeHorizontalBg />}
                        />

                        {/* Bottom row — 50/50 */}
                        <div className="col-span-3 grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {/* Animated Beam – Fluxo IA */}
                            <BentoCard
                                name="Fluxo da automatização"
                                description="Veja como funciona a mágica acontecer"
                                className="col-span-1 h-full"
                                Icon={({ className: c }) => (
                                    <span className={`material-symbols-outlined text-primary text-4xl ${c || ''}`}>conversion_path</span>
                                )}
                                href="#contato"
                                cta="Fale conosco"
                                background={
                                    <div className="absolute inset-0">
                                        <AnimatedBeamCard />
                                    </div>
                                }
                            />

                            {/* Exemplo de Mensagens */}
                            <BentoCard
                                name="Exemplo de mensagens"
                                description="Veja como o PapIA conversa com seus clientes no WhatsApp."
                                className="col-span-1 h-full"
                                Icon={({ className: c }) => (
                                    <span className={`material-symbols-outlined text-primary text-4xl ${c || ''}`}>chat_bubble</span>
                                )}
                                href="#contato"
                                cta="Fale conosco"
                                background={<ChatDemoCard />}
                            />
                        </div>
                    </BentoGrid>

                    {/* Connecting line */}
                    <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-px h-24 bg-gradient-to-b from-primary/40 to-transparent z-0"></div>
                </Section>

                {/* �"?�"? FEATURES �"?�"? */}
                <Section
                    id="recursos"
                    className="relative py-32 px-8 bg-surface overflow-visible"
                >
                    {/* Aura background */}
                    <div className="absolute inset-0 z-0 flex items-center justify-center overflow-hidden pointer-events-none">
                        <div className="w-[800px] h-[600px] rounded-[100%] blur-[120px] opacity-60" style={{ backgroundColor: 'rgba(81,225,165,0.10)' }}></div>
                        <div className="absolute top-0 right-0 w-[400px] h-[400px] rounded-full blur-[100px] -translate-y-1/2 translate-x-1/4" style={{ backgroundColor: 'rgba(209,192,255,0.05)' }}></div>
                        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[2px] h-32 bg-gradient-to-b from-primary/30 via-primary/10 to-transparent"></div>
                        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[2px] h-32 bg-gradient-to-t from-primary/30 via-primary/10 to-transparent"></div>
                    </div>

                    <div className="relative z-10 grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
                        {/* Feature list */}
                        <div className="order-2 md:order-1">
                            <div className="space-y-12">
                                {[
                                    {
                                        icon: 'auto_fix_high',
                                        title: 'Automação inteligente de respostas',
                                        desc: 'Respostas automáticas com IA para atender seus clientes com agilidade.',
                                    },
                                    {
                                        icon: 'data_exploration',
                                        title: 'Dashboard com analytics em tempo real',
                                        desc: 'Acompanhe métricas, performance e resultados com gráficos atualizados.',
                                    },
                                    {
                                        icon: 'language',
                                        title: 'Maestria Multilíngue',
                                        desc: 'Comunique-se globalmente com fluência nativa em mais de 50 idiomas.',
                                    },
                                ].map((f, i) => (
                                    <motion.div
                                        key={f.title}
                                        className="flex gap-6 group"
                                        variants={fadeUp}
                                        custom={i}
                                    >
                                        <div className="flex-shrink-0 w-12 h-12 bg-surface-container flex items-center justify-center rounded border border-outline-variant/20 group-hover:border-primary transition-colors">
                                            <span className="material-symbols-outlined text-primary">{f.icon}</span>
                                        </div>
                                        <div>
                                            <h4 className="font-bold text-xl mb-2">{f.title}</h4>
                                            <p className="text-on-surface-variant text-sm leading-relaxed">{f.desc}</p>
                                        </div>
                                    </motion.div>
                                ))}
                            </div>
                        </div>

                        {/* Right text + code block */}
                        <div className="order-1 md:order-2 space-y-6">
                            <motion.div
                                className="inline-block px-3 py-1 rounded-full border border-primary/20 bg-primary/5 text-[10px] font-bold text-primary tracking-widest uppercase"
                                variants={fadeUp}
                            >
                                Tecnologia de Ponta
                            </motion.div>
                            <motion.h2
                                className="text-4xl md:text-5xl font-extrabold tracking-tighter font-headline"
                                variants={fadeUp}
                                custom={1}
                            >

                            </motion.h2>
                            <motion.p
                                className="text-on-surface-variant leading-relaxed text-lg"
                                variants={fadeUp}
                                custom={2}
                            >
                                Cada interação é uma oportunidade crítica. PapIA garante que nenhum lead seja
                                perdido, com precisão cirúrgica em cada resposta.
                            </motion.p>
                            <motion.div
                                className="p-8 bg-surface-container/60 backdrop-blur-md rounded-xl border border-outline-variant/10 relative overflow-hidden group"
                                variants={fadeUp}
                                custom={3}
                            >
                                <div className="absolute top-0 right-0 w-32 h-32 bg-primary/10 rounded-full blur-3xl transition-opacity group-hover:opacity-100 opacity-50"></div>
                                <p className="text-xs font-mono text-primary mb-4">{'// PapIA_Status.js'}</p>
                                <ul className="space-y-3 text-xs font-mono text-on-surface-variant">
                                    <li className="flex items-center gap-2">
                                        <span className="text-primary">&gt;</span> Disponibilidade:{' '}
                                        <span className="text-white">24h / 7 dias</span>
                                    </li>
                                    <li className="flex items-center gap-2">
                                        <span className="text-primary">&gt;</span> Tempo de resposta:{' '}
                                        <span className="text-white">&lt; 10 segundos</span>
                                    </li>
                                    <li className="flex items-center gap-2">
                                        <span className="text-primary">&gt;</span> Compatível com:{' '}
                                        <span className="text-white">Mensagens de texto e voz</span>
                                    </li>
                                    <li className="flex items-center gap-2">
                                        <span className="text-primary">&gt;</span> Taxa de necessidade de atendimento humano:{' '}
                                        <span className="text-white">8%</span>
                                    </li>
                                </ul>
                            </motion.div>
                        </div>
                    </div>
                </Section>

                {/* PRICING */}
                <Section
                    id="planos"
                    className="clip-diagonal bg-surface-container-low px-8 pt-8 pb-32 relative"
                    style={{ clipPath: 'polygon(0 0, 100% 0, 100% 85%, 0% 100%)' }}
                >
                    {/* Connecting line */}
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-px h-20 bg-gradient-to-b from-transparent to-primary/40 z-0"></div>

                    <div className="text-center mb-6 relative z-10">
                        <motion.h2
                            className="text-3xl font-extrabold tracking-tight mb-4 font-headline"
                            variants={fadeUp}
                        >
                            Planos e preços
                        </motion.h2>
                        <motion.p
                            className="text-on-surface-variant text-sm uppercase tracking-widest font-semibold"
                            variants={fadeUp}
                            custom={1}
                        >
                            Escolha o melhor plano para o seu negócio
                        </motion.p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-12 max-w-5xl mx-auto justify-center px-4 md:px-12 py-4 relative z-10">

                        {/* Professional */}
                        <motion.div
                            className="bg-surface p-10 rounded-xl flex flex-col shadow-2xl relative overflow-hidden"
                            variants={fadeUp}
                            custom={0}
                            whileHover={{ y: -8 }}
                            transition={{ duration: 0.3 }}
                        >
                            <ShineBorder
                                borderWidth={1.5}
                                duration={10}
                                shineColor={['#51e1a5', '#67e8f9', '#51e1a5']}
                            />
                            <div className="mb-8">
                                <span className="text-[10px] font-bold text-primary border border-primary/30 px-2 py-0.5 rounded">
                                    COMECE AQUI
                                </span>
                                <h3 className="text-2xl font-bold mt-4 font-headline">Integração WhatsApp ou Instagram</h3>
                                <div className="flex items-baseline gap-1 mt-4">
                                    <span className="text-sm font-medium text-on-surface-variant">R$</span>
                                    <span className="text-5xl font-black tracking-tighter font-headline">89,90</span>
                                    <span className="text-sm font-medium text-on-surface-variant">/mês</span>
                                </div>
                            </div>
                            <ul className="space-y-4 mb-8 flex-1">
                                {[
                                    'IA conversacional',
                                    'Dashboard com analytics',
                                    'Relatórios de performance',
                                ].map((item) => (
                                    <li key={item} className="flex items-center gap-3 text-sm">
                                        <span className="material-symbols-outlined text-primary text-lg">check_circle</span>
                                        <span>{item}</span>
                                    </li>
                                ))}
                            </ul>
                            <HoverButton
                                onClick={scrollToContact}
                                className="w-full py-4 font-extrabold border-primary/30"
                                bgColor="var(--color-primary, #51e1a5)"
                                textColor="var(--color-on-primary, #003824)"
                                dotColor="#001f13"
                                textHoverColor="#fff"
                                showDot={false}
                                style={{ boxShadow: '0 4px 16px rgba(81,225,165,0.25)' }}
                            >
                                Assinar Professional
                            </HoverButton>
                        </motion.div>

                        {/* Enterprise */}
                        <motion.div
                            className="bg-surface p-10 rounded-xl flex flex-col relative overflow-hidden shadow-2xl"
                            variants={fadeUp}
                            custom={1}
                            whileHover={{ y: -8 }}
                            transition={{ duration: 0.3 }}
                        >
                            <ShineBorder
                                borderWidth={1.5}
                                duration={10}
                                shineColor={['#d1c0ff', '#f472b6', '#818cf8']}
                            />
                            <div className="absolute -top-12 -right-12 w-24 h-24 bg-tertiary/10 rotate-45"></div>
                            <div className="mb-8">
                                <span className="text-[10px] font-bold text-tertiary border border-tertiary/30 px-2 py-0.5 rounded">
                                    MAIS POPULAR
                                </span>
                                <h3 className="text-2xl font-bold mt-4 font-headline">Integração WhatsApp + Instagram</h3>
                                <div className="flex items-baseline gap-1 mt-4">
                                    <span className="text-sm font-medium text-on-surface-variant">R$</span>
                                    <span className="text-5xl font-black tracking-tighter font-headline">149,90</span>
                                    <span className="text-sm font-medium text-on-surface-variant">/mês</span>
                                </div>
                            </div>
                            <ul className="space-y-4 mb-8 flex-1">
                                {[
                                    { label: 'Todos os recursos do plano individual', highlight: false },
                                    { label: 'Automação em ambos canais', highlight: false },
                                    { label: ' Relatórios unificados', highlight: false },
                                    { label: 'Suporte prioritário 24/7', highlight: true },
                                ].map(({ label, highlight }) => (
                                    <li
                                        key={label}
                                        className={`flex items-center gap-3 text-sm${highlight ? ' font-bold text-tertiary' : ''}`}
                                    >
                                        <span className={`material-symbols-outlined text-lg${highlight ? '' : ' text-tertiary'}`}>
                                            {highlight ? 'bolt' : 'check_circle'}
                                        </span>
                                        <span>{label}</span>
                                    </li>
                                ))}
                            </ul>
                            <HoverButton
                                onClick={scrollToContact}
                                className="w-full py-4 font-extrabold border-tertiary/30"
                                bgColor="var(--color-tertiary, #d1c0ff)"
                                textColor="var(--color-on-tertiary, #1a0060)"
                                dotColor="#0e0038"
                                textHoverColor="#fff"
                                showDot={false}
                                style={{ boxShadow: '0 4px 16px rgba(209,192,255,0.2)' }}
                            >
                                Assinar Enterprise
                            </HoverButton>
                        </motion.div>
                    </div>
                </Section>

                {/* �"?�"? CONTACT �"?�"? */}
                <Section id="contato" className="py-24 px-8 bg-surface contact-section">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-12 max-w-[1365px] mx-auto">

                        {/* Left: Form */}
                        <motion.div
                            className="bg-surface-container-high p-8 rounded-xl border border-outline-variant/20 shadow-2xl relative overflow-hidden contact-form"
                            variants={fadeUp}
                            custom={0}
                        >
                            {/* Top accent bar */}
                            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary to-primary-container"></div>

                            <div className="mb-8">
                                <h2 className="text-3xl font-extrabold tracking-tighter text-white font-headline">
                                    Envie sua mensagem
                                </h2>
                                <p className="text-on-surface-variant text-sm mt-2">
                                    Preencha o formulário abaixo e entraremos em contato em até 2 horas.
                                </p>
                            </div>

                            <form onSubmit={handleSubmit} className="space-y-6">
                                {/* Name */}
                                <div className="space-y-1">
                                    <label htmlFor="fullName" className="text-[10px] uppercase font-bold tracking-widest text-on-surface-variant ml-1">
                                        Nome *
                                    </label>
                                    <div className="uiverse-control">
                                        <span className="material-symbols-outlined absolute left-0 top-1/2 -translate-y-1/2 text-on-surface-variant text-sm pointer-events-none">person</span>
                                        <input
                                            type="text"
                                            id="fullName"
                                            name="fullName"
                                            value={formData.fullName}
                                            onChange={handleInputChange}
                                            placeholder="Seu nome completo"
                                            className={`input-alt${formErrors.fullName ? ' input-error' : ''}`}
                                            aria-describedby={formErrors.fullName ? 'fullName-error' : undefined}
                                        />
                                        <span className="input-border-alt" />
                                    </div>
                                    {formErrors.fullName && (
                                        <p id="fullName-error" className="mt-1 text-xs text-red-400 error-message">{formErrors.fullName}</p>
                                    )}
                                </div>

                                {/* Email + Phone */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 form-grid">
                                    <div className="space-y-1">
                                        <label htmlFor="email" className="text-[10px] uppercase font-bold tracking-widest text-on-surface-variant ml-1">
                                            Email *
                                        </label>
                                        <div className="uiverse-control">
                                            <span className="material-symbols-outlined absolute left-0 top-1/2 -translate-y-1/2 text-on-surface-variant text-sm pointer-events-none">mail</span>
                                            <input
                                                type="email"
                                                id="email"
                                                name="email"
                                                value={formData.email}
                                                onChange={handleInputChange}
                                                placeholder="seu@email.com"
                                                className={`input-alt${formErrors.email ? ' input-error' : ''}`}
                                                aria-describedby={formErrors.email ? 'email-error' : undefined}
                                            />
                                            <span className="input-border-alt" />
                                        </div>
                                        {formErrors.email && (
                                            <p id="email-error" className="mt-1 text-xs text-red-400 error-message">{formErrors.email}</p>
                                        )}
                                    </div>
                                    <div className="space-y-1">
                                        <label htmlFor="phone" className="text-[10px] uppercase font-bold tracking-widest text-on-surface-variant ml-1">
                                            Telefone *
                                        </label>
                                        <div className="uiverse-control">
                                            <span className="material-symbols-outlined absolute left-0 top-1/2 -translate-y-1/2 text-on-surface-variant text-sm pointer-events-none">call</span>
                                            <input
                                                type="tel"
                                                id="phone"
                                                name="phone"
                                                value={formData.phone}
                                                onChange={handleInputChange}
                                                placeholder="(99) 99999-9999"
                                                className={`input-alt${formErrors.phone ? ' input-error' : ''}`}
                                                aria-describedby={formErrors.phone ? 'phone-error' : undefined}
                                            />
                                            <span className="input-border-alt" />
                                        </div>
                                        {formErrors.phone && (
                                            <p id="phone-error" className="mt-1 text-xs text-red-400 error-message">{formErrors.phone}</p>
                                        )}
                                    </div>
                                </div>

                                {/* Company */}
                                <div className="space-y-1">
                                    <label htmlFor="company" className="text-[10px] uppercase font-bold tracking-widest text-on-surface-variant ml-1">
                                        Empresa
                                    </label>
                                    <div className="uiverse-control">
                                        <span className="material-symbols-outlined absolute left-0 top-1/2 -translate-y-1/2 text-on-surface-variant text-sm pointer-events-none">business</span>
                                        <input
                                            type="text"
                                            id="company"
                                            name="company"
                                            value={formData.company}
                                            onChange={handleInputChange}
                                            placeholder="Nome da sua empresa"
                                            className="input-alt"
                                        />
                                        <span className="input-border-alt" />
                                    </div>
                                </div>

                                {/* Message */}
                                <div className="space-y-1">
                                    <label htmlFor="message" className="text-[10px] uppercase font-bold tracking-widest text-on-surface-variant ml-1">
                                        Mensagem *
                                    </label>
                                    <div className="uiverse-control">
                                        <span className="material-symbols-outlined absolute left-0 top-4 text-on-surface-variant text-sm pointer-events-none">chat_bubble</span>
                                        <textarea
                                            id="message"
                                            name="message"
                                            value={formData.message}
                                            onChange={handleInputChange}
                                            rows={4}
                                            placeholder="Conte-nos sobre suas necessidades..."
                                            className={`input-alt textarea-alt${formErrors.message ? ' input-error' : ''}`}
                                            aria-describedby={formErrors.message ? 'message-error' : undefined}
                                        />
                                        <span className="input-border-alt" />
                                    </div>
                                    {formErrors.message && (
                                        <p id="message-error" className="mt-1 text-xs text-red-400 error-message">{formErrors.message}</p>
                                    )}
                                </div>

                                {/* Submit */}
                                <HoverButton
                                    type="submit"
                                    disabled={isSubmitting}
                                    onClick={undefined}
                                    className={`submit-button w-full py-4 font-extrabold border-primary/30${isSubmitting ? ' opacity-60 cursor-not-allowed pointer-events-none' : ''}`}
                                    bgColor={isSubmitting ? 'var(--color-surface-container, #201f21)' : 'var(--color-primary, #51e1a5)'}
                                    textColor={isSubmitting ? 'var(--color-on-surface-variant, #bbcbb8)' : 'var(--color-on-primary, #003824)'}
                                    dotColor={isSubmitting ? '#333' : '#001f13'}
                                    textHoverColor="#fff"
                                    showDot={false}
                                    style={!isSubmitting ? { boxShadow: '0 4px 16px rgba(81,225,165,0.25)' } : {}}
                                >
                                    {isSubmitting ? 'Enviando...' : 'Enviar Mensagem'}
                                </HoverButton>

                                {submitStatus === 'error' && (
                                    <motion.div
                                        initial={{ opacity: 0, y: 8 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        className="p-3.5 bg-red-500/10 border border-red-500/30 rounded-xl error-message"
                                    >
                                        <p className="text-red-400 text-sm">
                                            Erro ao enviar mensagem. Verifique os campos e tente novamente.
                                        </p>
                                    </motion.div>
                                )}
                            </form>
                        </motion.div>

                        {/* Right: Info */}
                        <div className="flex flex-col justify-between py-4 contact-info">
                            <div className="space-y-6">
                                <motion.h2
                                    className="text-4xl font-extrabold tracking-tighter text-white font-headline"
                                    variants={fadeUp}
                                >
                                    Vamos <span className="text-primary">Conversar?</span>
                                </motion.h2>
                                <motion.p className="text-on-surface-variant leading-relaxed" variants={fadeUp} custom={1}>
                                    Estamos sempre disponíveis para discutir como nossas soluções podem transformar
                                    seu negócio. Entre em contato conosco através dos canais abaixo ou preencha o
                                    formulário ao lado.
                                </motion.p>

                                <div className="space-y-4 pt-4">
                                    <h3 className="text-xs uppercase font-bold tracking-[0.2em] text-white">
                                        Informações de Contato
                                    </h3>

                                    {/* WhatsApp */}
                                    <motion.div
                                        className="flex items-center gap-4 group cursor-pointer contact-info-card"
                                        variants={fadeUp}
                                        custom={2}
                                        whileHover={{ x: 4 }}
                                    >
                                        <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center border border-primary/20 group-hover:bg-primary group-hover:text-on-primary transition-all">
                                            <span className="material-symbols-outlined">call</span>
                                        </div>
                                        <div>
                                            <p className="font-bold text-white">WhatsApp</p>
                                            <p className="text-sm text-on-surface-variant">(41) 9 8771-8296</p>
                                            <p className="text-[10px] text-primary/60 font-medium">Disponível das 9h às 18h</p>
                                        </div>
                                    </motion.div>

                                    {/* Email */}
                                    <motion.div
                                        className="flex items-center gap-4 group cursor-pointer contact-info-card"
                                        variants={fadeUp}
                                        custom={3}
                                        whileHover={{ x: 4 }}
                                    >
                                        <div className="w-12 h-12 rounded-lg bg-tertiary/10 flex items-center justify-center border border-tertiary/20 group-hover:bg-tertiary group-hover:text-on-tertiary transition-all">
                                            <span className="material-symbols-outlined">mail</span>
                                        </div>
                                        <div>
                                            <p className="font-bold text-white">Email</p>
                                            <p className="text-sm text-on-surface-variant">contato@aytech.online</p>
                                            <p className="text-[10px] text-tertiary/60 font-medium">Resposta em até 2 horas</p>
                                        </div>
                                    </motion.div>
                                </div>
                            </div>

                            {/* Social card */}
                            <motion.div
                                className="mt-12 p-8 bg-gradient-to-br from-primary to-primary-container rounded-xl text-on-primary shadow-xl"
                                variants={fadeUp}
                                custom={4}
                            >
                                <h4 className="font-extrabold text-xl tracking-tighter mb-2 font-headline">
                                    Siga-nos nas Redes Sociais
                                </h4>
                                <p className="text-sm text-on-primary/80 mb-6">
                                    Acompanhe nossas novidades, dicas e casos de sucesso.
                                </p>
                                <div className="flex gap-3">
                                    {/* Instagram */}
                                    <motion.a
                                        href="https://instagram.com/aytechcwb"
                                        target="_blank"
                                        rel="noreferrer"
                                        aria-label="Instagram"
                                        className="social-link w-10 h-10 rounded-md bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors"
                                        whileHover={{ scale: 1.1, y: -2 }}
                                        whileTap={{ scale: 0.9 }}
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
                                            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                                        </svg>
                                    </motion.a>
                                    {/* Facebook */}
                                    <motion.a
                                        href="https://facebook.com/aytechcwb"
                                        target="_blank"
                                        rel="noreferrer"
                                        aria-label="Facebook"
                                        className="social-link w-10 h-10 rounded-md bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors"
                                        whileHover={{ scale: 1.1, y: -2 }}
                                        whileTap={{ scale: 0.9 }}
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
                                            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                                        </svg>
                                    </motion.a>
                                </div>
                            </motion.div>
                        </div>
                    </div>
                </Section>

            </main>

            {/* FOOTER */}
            <footer className="border-t border-white/5 pt-6 pb-4 md:pt-16 md:pb-8" style={{ backgroundColor: '#131315' }}>
                <div className="mx-auto px-6 md:px-8 max-w-[1365px]">
                    <div className="grid grid-cols-2 md:grid-cols-12 gap-6 md:gap-12 mb-6 md:mb-16">

                        {/* Brand */}
                        <div className="col-span-2 md:col-span-4 space-y-3 md:space-y-6">
                            <div className="space-y-1">
                                <h2 className="text-2xl md:text-3xl font-black tracking-tighter text-primary font-headline">
                                    AyTech
                                </h2>
                                <p className="text-on-surface-variant text-xs font-bold uppercase tracking-widest">
                                    Soluções Inovadoras
                                </p>
                            </div>
                            <p className="hidden md:block text-on-surface-variant text-sm leading-relaxed max-w-xs">
                                Transformamos negócios através da tecnologia, oferecendo soluções inovadoras em
                                automação, inteligência artificial e gestão de dados.
                            </p>
                            <div className="flex gap-4 md:gap-6">
                                <a
                                    href="https://instagram.com/aytechcwb"
                                    target="_blank"
                                    rel="noreferrer"
                                    aria-label="Instagram"
                                    className="text-on-surface-variant hover:text-primary transition-colors"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                                    </svg>
                                </a>
                                <a
                                    href="https://facebook.com/aytechcwb"
                                    target="_blank"
                                    rel="noreferrer"
                                    aria-label="Facebook"
                                    className="text-on-surface-variant hover:text-primary transition-colors"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                                    </svg>
                                </a>
                            </div>
                        </div>

                        {/* Quick links */}
                        <div className="md:col-span-2 space-y-3 md:space-y-6">
                            <h3 className="text-white font-bold tracking-tight">Links Rápidos</h3>
                            <ul className="space-y-2 md:space-y-3">
                                {[
                                    { label: 'Início', action: () => window.scrollTo({ top: 0, behavior: 'smooth' }) },
                                    { label: 'Recursos', action: () => scrollTo('recursos') },
                                    { label: 'Fale Conosco', action: () => scrollTo('contato') },
                                ].map((link) => (
                                    <li key={link.label}>
                                        <button
                                            onClick={link.action}
                                            className="text-on-surface-variant hover:text-primary text-sm transition-colors"
                                        >
                                            {link.label}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* Services */}
                        <div className="md:col-span-3 space-y-3 md:space-y-6">
                            <h3 className="text-white font-bold tracking-tight">Nossos Serviços</h3>
                            <ul className="space-y-2 md:space-y-3">
                                {[
                                    { label: 'Automação IA WhatsApp', action: () => window.scrollTo({ top: 0, behavior: 'smooth' }) },
                                    { label: 'Criador de Rotas', href: 'https://rotas.aytech.online' },
                                    { label: 'Financeiro Inteligente', href: 'https://lucrum.aytech.online' },
                                ].map((item) => (
                                    <li key={item.label}>
                                        {item.href ? (
                                            <a
                                                href={item.href}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="text-on-surface-variant hover:text-primary text-sm transition-colors"
                                            >
                                                {item.label}
                                            </a>
                                        ) : (
                                            <button
                                                onClick={item.action}
                                                className="text-on-surface-variant hover:text-primary text-sm transition-colors"
                                            >
                                                {item.label}
                                            </button>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* Contact */}
                        <div className="col-span-2 md:col-span-3 space-y-3 md:space-y-6">
                            <h3 className="text-white font-bold tracking-tight">Contato</h3>
                            <ul className="space-y-2 md:space-y-4">
                                <li className="flex items-start gap-3 text-sm text-on-surface-variant">
                                    <span className="material-symbols-outlined text-primary">location_on</span>
                                    <span>Curitiba, Paraná</span>
                                </li>
                                <li className="flex items-center gap-3 text-sm text-on-surface-variant">
                                    <span className="material-symbols-outlined text-primary">call</span>
                                    <span>(41) 9 8771-8296</span>
                                </li>
                                <li className="flex items-center gap-3 text-sm text-on-surface-variant">
                                    <span className="material-symbols-outlined text-primary">mail</span>
                                    <span>contato@aytech.online</span>
                                </li>
                            </ul>
                            <div className="pt-2 md:pt-4">
                                <h4 className="text-white text-xs font-bold uppercase tracking-widest mb-2">
                                    Horário de Atendimento:
                                </h4>
                                <p className="text-xs text-on-surface-variant">Segunda a Sexta: 9h às 18h</p>
                                <p className="text-xs text-on-surface-variant">
                                    Finais de semana e feriados: 12h às 18h
                                </p>
                            </div>
                        </div>

                    </div>

                    {/* Bottom bar */}
                    <div className="pt-3 md:pt-8 border-t border-white/5 text-center">
                        <p className="text-xs text-on-surface-variant/60 tracking-tight">
                            &copy; 2025 AyTech - Soluções Tecnológicas. Todos os direitos reservados.
                        </p>
                    </div>
                </div>
            </footer>

            {/* Success Modal */}
            <SuccessModal isOpen={showSuccessModal} onClose={() => setShowSuccessModal(false)} />
        </div>
    );
};

export default LandingPage;
